import React, { useEffect, useState } from "react";
import { Plus, Edit, Trash2 } from "lucide-react";
import Modal from "../components/Common/Modal";
import { HistologyReport } from "../types";
import {
  getHistologyReportsByPatientId,
  createHistologyReport,
  updateHistologyReport,
  deleteHistologyReport,
  viewHistologyReport,
} from "../services/histologyReportServices";
import { useAuth } from "../contexts/AuthContext";
import { ROLES } from "../constants/roles"; 
import { toast } from "react-toastify";

interface HistologyReportsProps {
  patientId: string;
  formatFileSize: (size: number) => string;
  onDataChanged: () => void;
}

const HistologyReports: React.FC<HistologyReportsProps> = ({
  patientId,
  formatFileSize,
  onDataChanged,
}) => {
  const [histologyReportsList, setHistologyReportsList] = useState<HistologyReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const { user } = useAuth();

  const [mode, setMode] = useState<"add" | "edit" | null>(null);

  const [isDeleteHistologyOpen, setIsDeleteHistologyOpen] = useState(false);
  const [selectedReport, setSelectedReport] =
  useState<HistologyReport | null>(null);
  const [existingFileName, setExistingFileName] = useState<string | null>(null);

  const [histTitle, setHistTitle] = useState("");
  const [histSpecimen, setHistSpecimen] = useState("");
  const [histFindings, setHistFindings] = useState("");
  const [histDescription, setHistDescription] = useState("");
  const [histFile, setHistFile] = useState<File | null>(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
  
        const [histologyRes] = await Promise.all([
          patientId ? getHistologyReportsByPatientId(patientId) : Promise.resolve([])
        ]);

        // setDoctors(doctorsRes);
        if (patientId) {
          setHistologyReportsList(histologyRes);
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
  
    loadData();
  }, [patientId]);
    

  /* ---------------- CREATE ---------------- */
  const handleAddReport = async () => {
    try {
      setSaving(true);
  
      const formData = new FormData();
      formData.append("patient_id", patientId);

      if(!histTitle || !histSpecimen || !histFindings || !histDescription) {
        toast.error("Please fill in all fields!");
        return;
      }
  
      // Only send doctor_id if NOT superadmin
      if (user?.role !== ROLES.SUPERADMIN && user?.id) {
        formData.append("doctor_id", String(user.id));
      }
  
      formData.append("title", histTitle || "Untitled Histology Report");
      formData.append("specimen", histSpecimen || "");
      formData.append(
        "histological_findings",
        histFindings || "Findings pending."
      );
      formData.append("notes", histDescription || "");
  
      if (histFile) {
        formData.append("file", histFile);
        formData.append("filesize", String(histFile.size));
      }
  
      const createdReport = await createHistologyReport(formData);
  
      setHistologyReportsList((prev) => [createdReport, ...prev]);
      onDataChanged();

      setMode(null);
      setSelectedReport(null);
      
    } catch (e) {
      console.error("Add histology report failed:", e);
    } finally {
      setSaving(false);
    }
  };
  
  /* ----------------- Delete ---------------- */
  const openDelete = (report: HistologyReport) => {
    setSelectedReport(report);
    setIsDeleteHistologyOpen(true);
  };

  const handleDelete = async () => {
    if (!selectedReport) return;
    try {
      await deleteHistologyReport(selectedReport.id);
      setHistologyReportsList((prev) =>
        prev.filter((r) => r.id !== selectedReport.id)
      );
      setIsDeleteHistologyOpen(false);
      onDataChanged();
      setSelectedReport(null);
    } catch (e) {
      console.error(e);
    }
  };

  /* ----------------- EDIT ---------------- */
  const openEdit = (report: HistologyReport) => {
    setSelectedReport(report);
  
    setHistTitle(report.title || "");
    setHistSpecimen(report.specimen || "");
    setHistFindings(report.histological_findings || "");
    setHistDescription(report.notes || "");
    setExistingFileName(report.url?.split("/").pop() || "");
    setHistFile(null);
  
    // open modal in edit mode
    setMode("edit");
  };
  
  const handleUpdate = async () => {
    if (!selectedReport) return;
  
    try {
      setSaving(true);

      if(!histTitle || !histSpecimen || !histFindings || !histDescription) {
        toast.error("Please fill in all fields!");
        return;
      }
      
      const updated = await updateHistologyReport(
        selectedReport.id,
        {
          title: histTitle,
          specimen: histSpecimen,
          histological_findings: histFindings,
          notes: histDescription,
        },
        histFile // optional new file
      );
  
      setHistologyReportsList((prev) =>
        prev.map((r) => (r.id === updated.id ? updated : r))
      );

      onDataChanged();
  
      // CLOSE MODAL CORRECTLY
      setMode(null);
      setSelectedReport(null);
    } catch (e) {
      console.error("Update histology report failed:", e);
    } finally {
      setSaving(false);
    }
  };
  

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold text-gray-900">
          Histology Reports
        </h3>
        <button
          onClick={() => {
            // reset form state
            setSelectedReport(null);
            setHistTitle("");
            setHistSpecimen("");
            setHistFindings("");
            setHistDescription("");
            setHistFile(null);

            setMode("add");
          }}
          className="btn-primary flex items-center gap-2"
        >
          <Plus size={16} />
          Add Histology Report
        </button>

      </div>

      {loading && <p className="text-gray-500">Loading...</p>}

      {/* Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {histologyReportsList.map((report) => (
          <div key={report.id} className="card relative p-4">
            {/* ACTION ICONS */}
            <div className="absolute top-4 right-4 flex gap-2">
              <button title="Edit"
                onClick={() => openEdit(report)}
                className="text-gray-400 hover:text-gray-700">
                <Edit size={16} />
              </button>
              <button title="Delete"
                onClick={() => openDelete(report)}
                className="text-red-400 hover:text-red-600">
                <Trash2 size={16} />
              </button>
            </div>

            <h4 className="font-semibold text-gray-900 pr-14"> {report.title} </h4>

            <div className="mt-1 space-y-1 text-xs text-gray-500">
              <div>
                Reported on: {new Date(report.createdAt).toLocaleDateString()}
              </div>
              <div>
                Specimen: {report.specimen || "-"}
              </div>
            </div>

            <p className="text-sm text-gray-600 mt-3">
              {report.histological_findings?.substring(0, 60)}
            </p>

            {report.notes && (
              <p className="text-sm text-gray-600 mt-2">
                {report.notes}
              </p>
            )}
                {/* FILE INFO */}
              <div className="flex justify-between items-start text-xs text-gray-500 mt-4 gap-2">
                <span className="flex-1 break-all">
                  {report.url?.split("/").pop() || "-"}
                </span>

                <span className="whitespace-nowrap">
                  {report.filesize
                      ? formatFileSize(report.filesize)
                      : "-"}
                </span>
              </div>


              <button
            onClick={async () => {
              try {
                const url = await viewHistologyReport(report.id);
                window.open(url, "_blank", "noopener,noreferrer");
              } catch (err: any) {
                toast.error(err.message || "Failed to open report");
              }
            }}
                className="w-full mt-3 btn-secondary text-sm"
              >
                View Report
              </button>
              
          </div>
        ))}
      </div>


      {/* Add and EDIT MODAL */}
      <Modal
        title={mode === "edit" ? "Edit Histology Report" : "Add Histology Report"}
        isOpen={mode !== null}
        onClose={() => {
          setMode(null);
          setSelectedReport(null);
        }}
      >

        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            mode === "edit" ? handleUpdate() : handleAddReport();
          }}
          
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Title <span className="text-red-500">*</span>
              </label>
              <input
                value={histTitle}
                onChange={(e) => setHistTitle(e.target.value)}
                className="input-field mt-1"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Specimen <span className="text-red-500">*</span>
              </label>
              <input
                value={histSpecimen}
                onChange={(e) => setHistSpecimen(e.target.value)}
                className="input-field mt-1"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Histological Findings <span className="text-red-500">*</span>
            </label>
            <textarea
              value={histFindings}
              onChange={(e) => setHistFindings(e.target.value)}
              className="input-field mt-1"
              rows={4}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Upload Report <span className="text-red-500">*</span>
            </label>
            <input
              type="file"
              accept=".pdf,.doc,.docx"
              onChange={(e) => {
                const selected = e.target.files?.[0] ?? null
                setHistFile(selected);
                if(selected){
                  setExistingFileName(selected.name)
                }
              }}
              className="mt-1"
              required = {mode !== "edit"} 
            />
            {existingFileName && (
              <p className="text-xs text-gray-600">
                Current file:{" "}
                <span className="font-medium">{existingFileName}</span>
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Notes <span className="text-red-500">*</span>
            </label>
            <textarea
              value={histDescription}
              onChange={(e) => setHistDescription(e.target.value)}
              className="input-field mt-1"
              rows={3}
              required
            />
          </div>

          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={() => {
                setMode(null);
                setSelectedReport(null);
              }}
              className="btn-secondary"
            >
              Cancel
            </button>

            <button type="submit" className="btn-primary" disabled={saving}>
              {saving
                ? "Saving..."
                : mode === "edit"
                ? "Update Histology Report"
                : "Add Histology Report"}
            </button>

          </div>
        </form>
      </Modal>

      {/* DELETE MODAL */}
      <Modal
        title="Delete Histology Report"
        isOpen={isDeleteHistologyOpen}
        onClose={() => setIsDeleteHistologyOpen(false)}
      >
        <p className="mb-4">
          Are you sure you want to delete{" "}
          <strong>{selectedReport?.title}</strong>?
        </p>

        <div className="flex justify-end gap-3">
          <button className="btn-secondary" onClick={() => setIsDeleteHistologyOpen(false)}>
            Cancel
          </button>
          <button className="btn-danger" onClick={handleDelete}>
            Delete
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default HistologyReports;



